import React, { useRef, useState, useEffect, useMemo } from "react";
import Draggable from "react-draggable";
import Swal from "sweetalert2";
import { FaCheck, FaTimes, FaSignature, FaCopy, FaEye } from "react-icons/fa";
import { ResizableBox } from "react-resizable";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Media,
} from "reactstrap";
import useContact from "../../context/hooks/useContact";
import { DocumentTypeContext } from "../../context/DocumentContext";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";

const DraggableText = ({
  id,
  onEnd,
  onCancel,
  onCopy,
  initialText,
  position,
  pageNum,
  totalNumPages,
  pageDetails,
  documentRef,
  typeSign = "Assinatura",
  dropdownOpen: initialDropdownOpen = false,
  setComponents,
  collaborators,
  onAddSignature,
}) => {
  const [text, setText] = useState(initialText || "Assinatura");
  const [showControls, setShowControls] = useState(true);
  const [type, setType] = useState(typeSign);
  const [dropdownOpen, setDropdownOpen] = useState(initialDropdownOpen);
  const inputRef = useRef(null);
  const signatureColor = useMemo(() => {
    const storedColor =
      localStorage.getItem(`signatureColor-${text}`) || getRandomColor();
    return storedColor;
  }, [text]);

  function getRandomColor() {
    const letters = "0123456789ABCDEF";
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  useEffect(() => {
    if (initialText) {
      setText(initialText);
    } else {
      inputRef.current && inputRef.current.focus();
      inputRef.current && inputRef.current.select();
    }
  }, [initialText]);

  useEffect(() => {
    localStorage.setItem(`signatureColor-${text}`, signatureColor);
  }, [text, signatureColor]);

  const getCollaboratorName = () => {
    const collaborator = collaborators?.find((c) => c.name === text);
    return collaborator ? collaborator.name : "";
  };

  const styles = {
    container: {
      position: "absolute",
      zIndex: 20,
      border: `2px solid ${signatureColor}`,
      padding: "0px 5px",
      borderRadius: "10px",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
      backgroundColor: "#f9f9f9",
      cursor: showControls ? "move" : "auto",
      width: "200px",
      height: "auto",
    },
    controls: {
      position: "absolute",
      top: "8px",
      right: "8px",
      display: showControls ? "flex" : "none",
      alignItems: "center",
    },
    smallButton: {
      cursor: "pointer",
      marginLeft: "8px",
      fontSize: "20px",
      border: "none",
      background: "transparent",
      "&:hover": {
        color: "#777",
      },
    },
    inputContainer: {
      display: "flex",
      alignItems: "center",
    },
    input: {
      border: 0,
      fontSize: 16,
      padding: "5px",
      background: "transparent",
      cursor: "move",
      width: "150px",
      marginRight: "8px",
      outline: "none",
      borderRadius: "5px",
      fontFamily: "'Dancing Script', cursive",
      color: "#555",
    },
    signatureIcon: {
      fontSize: 20,
      color: signatureColor,
      marginRight: "8px",
    },
    colaboradorContainer: {
      fontSize: 14,
      color: "#888",
      marginTop: "5px",
    },
    copyButton: {
      cursor: "pointer",
      marginRight: "8px",
      fontSize: "20px",
      border: "none",
      background: "transparent",
      "&:hover": {
        color: "#777",
      },
    },
  };

  const handleSelectType = (selectedType) => {

    if (!selectedCollaborator) {
      return;
    }

    setType(selectedType);
    setDropdownOpen(false);
    const storageKey = `${id}-typeSign`;
    localStorage.setItem(storageKey, selectedType);

    if (selectedCollaborator?.name) {
      onAddSignature(
        "",
        selectedCollaborator.name,
        selectedCollaborator.email,
        selectedCollaborator.phoneNumber,
        selectedCollaborator.addToContacts,
        selectedCollaborator.sendMessageWhatsApp,
        selectedCollaborator.requireSignerDocument,
        selectedCollaborator.signPosition,
        selectedType
      );
    }
  };

  const handleCopy = (id, pageNum) => {
    Swal.fire({
      title: "Selecione uma opção de cópia",
      showCancelButton: true,
      confirmButtonText: "Copiar em todas as páginas",
      showCloseButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        onCopy(type);
      }
    });
  };

  const [selectedCollaborator, setSelectedCollaborator] = useState(null);

  const handleSelectCollaborator = (collaborator) => {
    setSelectedCollaborator(collaborator);
  };

  return (
    <Draggable
      onStart={() => setShowControls(true)}
      onStop={(e, data) => {
        onEnd(e, data, id, pageNum, typeSign);
      }}
      defaultPosition={position}
      bounds={{
        top: 0 - 20,
        right: documentRef.current?.clientWidth - 100 || 0,
        bottom: documentRef.current?.clientHeight - 20 || 0,
        left: 0 - 100,
      }}
    >
      <div style={styles.container}>
        {dropdownOpen ? (
          <div
            style={{
              marginTop: "10px",
              marginBottom: "10px",
              padding: "10px",
              position: "relative",
            }}
            
          >
            <button
              style={{
                position: "absolute",
                top: "5px",
                right: "5px",
                background: "none",
                border: "none",
                cursor: "pointer",
                zIndex: "2",
                padding: "8px",
              }}
              onMouseUp={(e) => {
                if (e && e.stopPropagation) e.stopPropagation();
              }}
              onClick={(e) => {
                if (e && e.stopPropagation) e.stopPropagation();
                onCancel(id);
              }}
            >
              <FaTimes color="#E57373" />
            </button>

            <FormControl fullWidth variant="standard" size="small">
              <InputLabel id="demo-simple-select-standard-label">
                Signatário(a)
              </InputLabel>
              <Select
                label="Signatário(a)"
                labelId="collaborator-select-label"
                value={selectedCollaborator?.id || ""}
                onChange={(e) => {
                  const selectedId = Number(e.target.value);
                  const collaborator = collaborators.find(
                    (col) => col.id === selectedId
                  );
                  if (collaborator) {
                    setSelectedCollaborator(collaborator);
                  }
                }}
              >
                {collaborators.map((collaborator) => (
                  <MenuItem key={collaborator.id} value={collaborator.id}>
                    {collaborator.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <div
              style={{
                backgroundColor: "#DDD",
                padding: "15px",
                borderRadius: "5px",
                marginTop: "10px",
                cursor: "pointer",
              }}
              onClick={() => handleSelectType("Assinatura")}
            >
              <FaSignature /> Assinatura
            </div>
            <div
              style={{
                backgroundColor: "#DDD",
                padding: "15px",
                borderRadius: "5px",
                marginTop: "5px",
                cursor: "pointer",
              }}
              onClick={() => handleSelectType("Visto")}
            >
              <FaEye /> Visto
            </div>
          </div>
        ) : (
          <>
            <div style={styles.controls}>
              <button
                style={styles.smallButton}
                onMouseUp={(e) => {
                  if (e && e.stopPropagation) e.stopPropagation();
                }}
                onClick={(e) => {
                  if (e && e.stopPropagation) e.stopPropagation();
                  onCancel(id);
                }}
              >
                <FaTimes color="#E57373" />
              </button>

              <button
                style={styles.copyButton}
                onMouseUp={(e) => {
                  if (e && e.stopPropagation) e.stopPropagation();
                }}
                onClick={(e) => {
                  if (e && e.stopPropagation) e.stopPropagation();
                  handleCopy(id, pageNum, typeSign);
                }}
              >
                <FaCopy />
              </button>
            </div>

            <div style={styles.inputContainer}>
              {type === "Assinatura" ? (
                <FaSignature size={20} />
              ) : (
                <FaEye size={20} />
              )}
              <input
                ref={inputRef}
                style={styles.input}
                placeholder={type}
                value={type}
                onChange={(e) => setText(e.target.value)}
                readOnly={!showControls}
              />
            </div>
            <div style={styles.colaboradorContainer}>
              {getCollaboratorName()}
            </div>
          </>
        )}
      </div>
    </Draggable>
  );
};

export default DraggableText;
